import React, { useState } from "react";
import useSWR from "swr";
import {
  Table,
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Select,
  message,
  Popconfirm,
  Button,
  Space,
  Row,
  Col,
  List,
  Avatar,
  Tag,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import {
  getSolutionList,
  createSolution,
  updateSolution,
  deleteSolution,
  getSolutionGroupSelect,
  searchProducts,
  getProductsByIds,
} from "../api/domainApi";
import { SolutionRes, AdminProductRes, RewardMapping } from "../types/domain";
import PageHeader from "../components/common/PageHeader";
import UseYnTag from "../components/common/UseYnTag";

const PAGE_SIZE = 20;

// 솔루션 유형 (sltnType) — SC SolutionType enum 과 동일. value=영문, label=한글노출
const SLTN_TYPE_OPTIONS = [
  { value: "PMND", label: "강아지마음 (PMND)" },
  { value: "SCMT", label: "설쌤코멘트 (SCMT)" },
  { value: "SLTN", label: "솔루션보상 (SLTN)" },
  { value: "TRNN", label: "트레이닝 (TRNN)" },
];

// 리워드 에디터 로컬 상태 (포인트/쿠폰ID 1쌍)
type RewardSlot = { point?: number | null; couponId?: number | null; clayfulCouponId?: string | null };

// 백엔드 rewards[] → 화면 상태 분해
const splitRewards = (rewards: RewardMapping[] | undefined, iterTarget: number) => {
  const complete: RewardSlot = {};
  const iter: RewardSlot[] = Array.from({ length: iterTarget }, () => ({}));
  (rewards ?? []).forEach((r) => {
    if (r.triggerType === "SOLUTION_COMPLETE") {
      if (r.rewardType === "POINT") complete.point = r.pointAmount;
      else if (r.rewardType === "COUPON") {
        complete.couponId = r.couponId;
        complete.clayfulCouponId = r.clayfulCouponId;
      }
    } else if (r.triggerType === "ITER_COMPLETE") {
      const idx = (r.iterSeq ?? 1) - 1;
      if (idx < 0) return;
      while (iter.length <= idx) iter.push({});
      if (r.rewardType === "POINT") iter[idx].point = r.pointAmount;
      else if (r.rewardType === "COUPON") {
        iter[idx].couponId = r.couponId;
        iter[idx].clayfulCouponId = r.clayfulCouponId;
      }
    }
  });
  return { complete, iter };
};

const SolutionPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState("");
  const [kwInput, setKwInput] = useState("");
  const [filterGrpId, setFilterGrpId] = useState<number | undefined>();
  const [filterType, setFilterType] = useState<string | undefined>();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<SolutionRes | null>(null);
  const [form] = Form.useForm();

  // 리워드 에디터 상태
  const [completeReward, setCompleteReward] = useState<RewardSlot>({});
  const [iterRewards, setIterRewards] = useState<RewardSlot[]>([]);
  const watchType = Form.useWatch("sltnType", form) as string | undefined;
  const watchIterTarget = Form.useWatch("sltnIterTarget", form) as
    | number
    | undefined;

  const setIterAt = (i: number, key: keyof RewardSlot, val: number | string | null) =>
    setIterRewards((prev) => {
      const next = [...prev];
      while (next.length <= i) next.push({});
      next[i] = { ...next[i], [key]: val };
      return next;
    });

  // 화면 상태 → 백엔드 rewards[] 조립
  const hasCoupon = (s: RewardSlot) =>
    s.couponId != null || (s.clayfulCouponId != null && s.clayfulCouponId.trim() !== "");
  const buildRewards = (type: string, iterTarget?: number): RewardMapping[] => {
    const out: RewardMapping[] = [];
    if (type === "SLTN") {
      if (completeReward.point != null && completeReward.point > 0)
        out.push({ triggerType: "SOLUTION_COMPLETE", iterSeq: null, rewardType: "POINT", pointAmount: completeReward.point, couponId: null, clayfulCouponId: null });
      if (hasCoupon(completeReward))
        out.push({ triggerType: "SOLUTION_COMPLETE", iterSeq: null, rewardType: "COUPON", pointAmount: null, couponId: completeReward.couponId ?? null, clayfulCouponId: completeReward.clayfulCouponId?.trim() || null });
    } else if (type === "TRNN") {
      // 회차(ITER_COMPLETE) 보상은 포인트만 — 쿠폰은 코스 완료(SLTN)에서만 발급되는 정책.
      for (let i = 0; i < (iterTarget ?? 0); i++) {
        const r = iterRewards[i] ?? {};
        if (r.point != null && r.point > 0)
          out.push({ triggerType: "ITER_COMPLETE", iterSeq: i + 1, rewardType: "POINT", pointAmount: r.point, couponId: null, clayfulCouponId: null });
      }
    }
    return out;
  };

  // 추천상품 선택 (OMS 프록시)
  const [selectedProducts, setSelectedProducts] = useState<AdminProductRes[]>([]);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [productKw, setProductKw] = useState("");
  const [productResults, setProductResults] = useState<AdminProductRes[]>([]);
  const [productSearching, setProductSearching] = useState(false);

  const runProductSearch = async (kw: string) => {
    setProductSearching(true);
    try {
      setProductResults(await searchProducts(kw));
    } catch (e: any) {
      message.error(e.message);
    } finally {
      setProductSearching(false);
    }
  };
  const doProductSearch = () => runProductSearch(productKw);
  const openProductPicker = () => {
    setProductKw("");
    setProductResults([]);
    setProductModalOpen(true);
    runProductSearch(""); // 열자마자 기본 상품 목록 로드
  };
  const addProduct = (p: AdminProductRes) =>
    setSelectedProducts((prev) =>
      prev.some((x) => x.productId === p.productId) ? prev : [...prev, p],
    );
  const removeProduct = (id: string) =>
    setSelectedProducts((prev) => prev.filter((p) => p.productId !== id));

  const swrKey = `/solution?page=${page}&kw=${keyword}&grp=${filterGrpId}&type=${filterType}`;
  const { data, isLoading, mutate } = useSWR(
    swrKey,
    () =>
      getSolutionList({
        page: page - 1,
        size: PAGE_SIZE,
        keyword,
        sltnGrpId: filterGrpId,
        sltnType: filterType,
      }),
    { keepPreviousData: true },
  );

  const { data: groups } = useSWR(
    "/solution-group/select",
    getSolutionGroupSelect,
  );

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setSelectedProducts([]);
    setCompleteReward({});
    setIterRewards([]);
    setModalOpen(true);
  };

  const openEdit = (row: SolutionRes) => {
    setEditing(row);
    form.setFieldsValue({
      ...row,
      sltnApplyStrDate: row.sltnApplyStrDate
        ? dayjs(row.sltnApplyStrDate)
        : null,
      sltnApplyEndDate: row.sltnApplyEndDate
        ? dayjs(row.sltnApplyEndDate)
        : null,
    });
    // 저장된 rewards → 리워드 에디터 상태 복원
    const { complete, iter } = splitRewards(
      row.rewards,
      row.sltnIterTarget ?? 0,
    );
    setCompleteReward(complete);
    setIterRewards(iter);
    // 저장된 clayfulProductIds CSV → 상품 객체 로드 (칩 표시용)
    const ids = (row.clayfulProductIds ?? "")
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    setSelectedProducts([]);
    getProductsByIds(ids)
      .then(setSelectedProducts)
      .catch(() => setSelectedProducts([]));
    setModalOpen(true);
  };

  const handleOk = async () => {
    const values = await form.validateFields();
    const type = values.sltnType as string;
    // 공통 필수 base
    const body: any = {
      sltnGrpId: values.sltnGrpId,
      sltnType: type,
      sltnTtl: values.sltnTtl,
      sltnSummary: values.sltnSummary,
      // 백엔드가 LocalDateTime 이라 시간까지 포함해야 함 (날짜만 보내면 파싱 에러)
      sltnApplyStrDate: values.sltnApplyStrDate?.format("YYYY-MM-DD[T]00:00:00"),
      sltnApplyEndDate: values.sltnApplyEndDate?.format("YYYY-MM-DD[T]23:59:59"),
      sortOrder: values.sortOrder,
    };
    // 유형별 필드 — 안 보이는 필드는 아예 안 보냄 (stale 방지)
    if (type === "PMND" || type === "SCMT") {
      body.sltnCnts = values.sltnCnts;
    } else if (type === "SLTN") {
      body.rewards = buildRewards(type);
    } else if (type === "TRNN") {
      body.sltnCnts = values.sltnCnts;
      body.sltnUrl = values.sltnUrl;
      body.vimeoId = values.vimeoId;
      body.vimeoHashParameter = values.vimeoHashParameter;
      body.sltnProduct = values.sltnProduct ?? "";
      body.clayfulProductIds = selectedProducts.map((p) => p.productId).join(",");
      body.sltnTrnnStep = values.sltnTrnnStep;
      body.sltnIterTarget = values.sltnIterTarget;
      body.rewards = buildRewards(type, values.sltnIterTarget);
    }
    try {
      if (editing) {
        await updateSolution(editing.id, body);
        message.success("수정되었습니다.");
      } else {
        await createSolution(body);
        message.success("추가되었습니다.");
      }
      setModalOpen(false);
      mutate();
    } catch (e: any) {
      message.error(e.message);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteSolution(id);
      message.success("삭제되었습니다.");
      mutate();
    } catch (e: any) {
      message.error(e.message);
    }
  };

  const columns: ColumnsType<SolutionRes> = [
    { title: "ID", dataIndex: "id", width: 70 },
    { title: "그룹", dataIndex: "sltnGrpTitle", width: 120 },
    { title: "유형", dataIndex: "sltnType", width: 80 },
    { title: "제목", dataIndex: "sltnTtl", ellipsis: { showTitle: true } },
    {
      title: "적용기간",
      width: 280,
      render: (_, r) => {
        const fmt = (v?: string) => v ? v.replace("T", " ").slice(0, 16) : "";
        return `${fmt(r.sltnApplyStrDate)} ~ ${fmt(r.sltnApplyEndDate)}`;
      },
    },
    { title: "순서", dataIndex: "sortOrder", width: 60 },
    {
      title: "사용여부",
      dataIndex: "useYn",
      width: 90,
      render: (v) => <UseYnTag value={v} />,
    },
    {
      title: "관리",
      width: 180,
      render: (_, row) => (
        <Space>
          <Button size="small" onClick={() => openEdit(row)}>
            수정
          </Button>
          <Popconfirm
            title="삭제하시겠습니까?"
            onConfirm={() => handleDelete(row.id)}
          >
            <Button size="small" danger>
              삭제
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageHeader title="솔루션" onAdd={openCreate} />
      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          placeholder="그룹 선택"
          allowClear
          style={{ width: 180 }}
          options={groups?.map((g) => ({ value: g.id, label: g.sltnGrpTitle }))}
          onChange={(v) => {
            setFilterGrpId(v);
            setPage(1);
          }}
        />
        <Select
          placeholder="유형 선택"
          allowClear
          style={{ width: 180 }}
          value={filterType}
          options={SLTN_TYPE_OPTIONS}
          onChange={(v) => {
            setFilterType(v);
            setPage(1);
          }}
        />
        <Input
          placeholder="제목/요약 검색"
          value={kwInput}
          onChange={(e) => setKwInput(e.target.value)}
          onPressEnter={() => {
            setKeyword(kwInput);
            setPage(1);
          }}
          style={{ width: 220 }}
        />
        <Button
          onClick={() => {
            setKeyword(kwInput);
            setPage(1);
          }}
        >
          검색
        </Button>
        <Button
          onClick={() => {
            setKwInput("");
            setKeyword("");
            setFilterGrpId(undefined);
            setFilterType(undefined);
            setPage(1);
          }}
        >
          초기화
        </Button>
      </Space>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={data?.content}
        loading={isLoading}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: data?.page?.totalElements,
          onChange: setPage,
          showSizeChanger: false,
        }}
        size="small"
      />
      <Modal
        title={editing ? "솔루션 수정" : "솔루션 추가"}
        open={modalOpen}
        onOk={handleOk}
        onCancel={() => setModalOpen(false)}
        okText={editing ? "수정" : "추가"}
        cancelText="취소"
        width={700}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="sltnGrpId"
                label="솔루션 그룹"
                rules={[{ required: true }]}
              >
                <Select
                  options={groups?.map((g) => ({
                    value: g.id,
                    label: g.sltnGrpTitle,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="sltnType"
                label="유형"
                rules={[{ required: true }]}
              >
                <Select placeholder="유형 선택" options={SLTN_TYPE_OPTIONS} />
              </Form.Item>
            </Col>
          </Row>
          {!watchType && (
            <div style={{ color: "#999", marginBottom: 12 }}>
              유형을 먼저 선택하면 해당 유형에 필요한 항목만 표시됩니다.
            </div>
          )}
          {/* 제목 / 요약 — 모든 유형 공통 */}
          {watchType && (
            <>
              <Form.Item name="sltnTtl" label="제목">
                <Input />
              </Form.Item>
              <Form.Item name="sltnSummary" label="요약">
                <Input />
              </Form.Item>
            </>
          )}
          {/* 본문 — SLTN 제외 (PMND/SCMT/TRNN) */}
          {(watchType === "PMND" ||
            watchType === "SCMT" ||
            watchType === "TRNN") && (
            <Form.Item name="sltnCnts" label="본문">
              <Input.TextArea rows={3} />
            </Form.Item>
          )}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="sltnApplyStrDate"
                label="적용 시작일"
                rules={[{ required: true }]}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="sltnApplyEndDate"
                label="적용 종료일"
                rules={[{ required: true }]}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="sortOrder"
                label="정렬 순서"
                rules={[{ required: true }]}
              >
                <InputNumber style={{ width: "100%" }} min={0} />
              </Form.Item>
            </Col>
            {watchType === "TRNN" && (
              <>
                <Col span={8}>
                  <Form.Item name="sltnTrnnStep" label="훈련 단계(차수)">
                    <InputNumber style={{ width: "100%" }} min={0} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="sltnIterTarget" label="반복 횟수">
                    <InputNumber style={{ width: "100%" }} min={0} />
                  </Form.Item>
                </Col>
              </>
            )}
          </Row>
          {/* TRNN 전용 — URL / Vimeo / 추천상품 */}
          {watchType === "TRNN" && (
            <>
              <Form.Item name="sltnUrl" label="URL">
                <Input />
              </Form.Item>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="vimeoId" label="Vimeo ID">
                    <Input placeholder="예: 76979871" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="vimeoHashParameter" label="Vimeo Hash (?h=)">
                    <Input placeholder="예: 8272103f6e" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="sltnProduct" label="추천 상품 OMS ID (수동, 숫자 CSV)">
                <Input placeholder="예: 123,456 (OMS 복귀 대비, 없으면 비움)" />
              </Form.Item>
              <Form.Item label="추천 상품 (Clayful)">
                <Button onClick={openProductPicker}>+ 상품 선택</Button>
                <div
                  style={{
                    marginTop: 8,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 8,
                  }}
                >
                  {selectedProducts.length === 0 && (
                    <span style={{ color: "#999" }}>선택된 상품 없음</span>
                  )}
                  {selectedProducts.map((p) => (
                    <Tag
                      key={p.productId}
                      closable
                      onClose={() => removeProduct(p.productId)}
                      color={p.saleStatus === "ON_SALE" ? "blue" : "red"}
                    >
                      {p.productName} (#{p.productId})
                      {p.saleStatus !== "ON_SALE" ? " · 품절/중단" : ""}
                    </Tag>
                  ))}
                </div>
              </Form.Item>
            </>
          )}

          {/* SLTN 전용 — 전체 보상 (코스 완료 시) */}
          {watchType === "SLTN" && (
            <Form.Item label="전체 보상 (코스 완료 시 지급)">
              <Space>
                <InputNumber
                  placeholder="포인트(트릿)"
                  min={0}
                  style={{ width: 160 }}
                  value={completeReward.point ?? undefined}
                  onChange={(v) =>
                    setCompleteReward((p) => ({ ...p, point: v as number | null }))
                  }
                />
                <InputNumber
                  placeholder="쿠폰 ID (선택)"
                  min={0}
                  style={{ width: 160 }}
                  value={completeReward.couponId ?? undefined}
                  onChange={(v) =>
                    setCompleteReward((p) => ({ ...p, couponId: v as number | null }))
                  }
                />
                <Input
                  placeholder="Clayful 쿠폰 ID (선택)"
                  style={{ width: 200 }}
                  value={completeReward.clayfulCouponId ?? undefined}
                  onChange={(e) =>
                    setCompleteReward((p) => ({ ...p, clayfulCouponId: e.target.value || null }))
                  }
                />
              </Space>
            </Form.Item>
          )}

          {/* TRNN 전용 — 회차별 보상 (회차 완료 시) */}
          {watchType === "TRNN" && (
            <Form.Item label="회차별 보상 (회차 완료 시 — 포인트만)">
              {(watchIterTarget ?? 0) > 0 ? (
                Array.from({ length: watchIterTarget as number }).map((_, i) => (
                  <Space key={i} style={{ display: "flex", marginBottom: 8 }}>
                    <span style={{ display: "inline-block", width: 56 }}>
                      {i + 1}회차
                    </span>
                    <InputNumber
                      placeholder="포인트"
                      min={0}
                      style={{ width: 140 }}
                      value={iterRewards[i]?.point ?? undefined}
                      onChange={(v) => setIterAt(i, "point", v as number | null)}
                    />
                  </Space>
                ))
              ) : (
                <span style={{ color: "#999" }}>
                  먼저 "반복 횟수"를 입력하세요.
                </span>
              )}
            </Form.Item>
          )}
        </Form>
      </Modal>

      {/* 추천상품 검색 피커 */}
      <Modal
        title={`추천 상품 선택 (Clayful) · 선택 ${selectedProducts.length}개`}
        open={productModalOpen}
        onCancel={() => setProductModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setProductModalOpen(false)}>
            닫기
          </Button>,
        ]}
        width={620}
      >
        <Space style={{ marginBottom: 12 }}>
          <Input
            placeholder="상품명/코드 검색"
            value={productKw}
            onChange={(e) => setProductKw(e.target.value)}
            onPressEnter={doProductSearch}
            style={{ width: 380 }}
          />
          <Button type="primary" onClick={doProductSearch} loading={productSearching}>
            검색
          </Button>
        </Space>
        <List
          dataSource={productResults}
          loading={productSearching}
          locale={{ emptyText: "상품이 없습니다" }}
          pagination={{ pageSize: 10, size: "small", showSizeChanger: false }}
          style={{ maxHeight: 460, overflowY: "auto" }}
          renderItem={(p) => {
            const picked = selectedProducts.some(
              (x) => x.productId === p.productId,
            );
            return (
              <List.Item
                actions={[
                  picked ? (
                    <Button
                      size="small"
                      danger
                      onClick={() => removeProduct(p.productId)}
                    >
                      제거
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      type="primary"
                      onClick={() => addProduct(p)}
                    >
                      추가
                    </Button>
                  ),
                ]}
              >
                <List.Item.Meta
                  avatar={
                    p.mainImageUrl ? (
                      <Avatar shape="square" size={48} src={p.mainImageUrl} />
                    ) : (
                      <Avatar shape="square" size={48}>
                        ?
                      </Avatar>
                    )
                  }
                  title={
                    <>
                      {p.productName}{" "}
                      {p.saleStatus !== "ON_SALE" && (
                        <Tag color="red">품절/중단</Tag>
                      )}
                    </>
                  }
                  description={`#${p.productId} · ${p.brandName ?? "-"} · ${
                    p.baseSalePrice != null
                      ? p.baseSalePrice.toLocaleString() + "원"
                      : "-"
                  }`}
                />
              </List.Item>
            );
          }}
        />
      </Modal>
    </div>
  );
};

export default SolutionPage;
